/**
 * ProjectDashboard — high-level overview of the current project's hiring pipeline.
 * Shows pipeline funnel, items needing attention, and recent activity.
 */
export class ProjectDashboard {
    constructor(options = {}) {
        this._db = options.db;
        this._projectStore = options.projectStore;
        this._candidateStore = options.candidateStore;
        this._promptStore = options.promptStore;
        this._statusBar = options.statusBar;
        this._onSelectCandidate = options.onSelectCandidate || (() => {});
        this._onSwitchTab = options.onSwitchTab || (() => {});
        this._projectId = null;
        this._el = document.createElement('div');
        this._el.className = 'rm-config-panel';
        this._unsubLog = null;
        this._logEntries = [];

        // Listen for candidate changes
        if (this._candidateStore) {
            this._candidateStore.onChange(() => this.render());
        }
    }

    get element() { return this._el; }

    setProject(projectId) {
        this._projectId = projectId;
        this._subscribeActivityLog();
        this.render();
    }

    _subscribeActivityLog() {
        if (this._unsubLog) this._unsubLog();
        this._unsubLog = null;
        this._logEntries = [];

        if (!this._projectId || !this._db) return;

        this._unsubLog = this._db.collection('function-logs')
            .where('projectId', '==', this._projectId)
            .orderBy('createdAt', 'desc')
            .limit(10)
            .onSnapshot(snap => {
                this._logEntries = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                this._renderActivity();
            }, err => {
                console.warn('Activity log subscription error:', err);
            });
    }

    render() {
        const candidates = this._candidateStore ? this._candidateStore.getAll() : [];

        if (!this._projectId) {
            this._el.innerHTML = `
                <div class="rm-empty-state">
                    <div class="rm-empty-state-icon">&#128202;</div>
                    <div>Open a project to see the dashboard</div>
                </div>`;
            return;
        }

        const project = this._projectStore?.getById(this._projectId);
        const stages = this._computePipelineStages(candidates);
        const attention = this._computeAttentionItems(candidates);

        this._el.innerHTML = '';

        // Project header
        if (project) {
            const header = document.createElement('div');
            header.style.cssText = 'font-size:16px;font-weight:600;color:var(--text);margin-bottom:4px;';
            header.textContent = project.name || 'Project Dashboard';
            this._el.appendChild(header);
        }

        // Pipeline section
        this._el.appendChild(this._buildPipeline(stages));

        // Needs Attention section
        this._el.appendChild(this._buildAttention(attention));

        // Recent Activity section
        const activitySection = document.createElement('div');
        activitySection.className = 'rm-config-section';
        activitySection.innerHTML = `<h3>Recent Activity</h3>`;
        const activityList = document.createElement('div');
        activityList.className = 'rm-activity-list';
        activitySection.appendChild(activityList);
        this._el.appendChild(activitySection);
        this._activityListEl = activityList;
        this._renderActivity();
    }

    _computePipelineStages(candidates) {
        const total = candidates.length;
        const extracted = candidates.filter(c => c.extractionStatus === 'complete').length;

        // Scored = has at least one completed score
        const scored = candidates.filter(c => {
            const scores = c.scores || {};
            return Object.values(scores).some(s => s?.status === 'complete');
        }).length;

        // Contact 1st = disposition is 'advance' or has been emailed (emailsSent > 0)
        const contacted = candidates.filter(c =>
            c.disposition === 'advance' || c.emailsSent > 0 || c.responseStatus
        ).length;

        // Interviewed = interview flag set
        const interviewed = candidates.filter(c => c.interview).length;

        // Hire = disposition is 'hire'
        const hired = candidates.filter(c => c.disposition === 'hire').length;

        return [
            { label: 'Total', count: total, color: '#64748b' },
            { label: 'Extracted', count: extracted, color: '#3b82f6' },
            { label: 'Scored', count: scored, color: '#8b5cf6' },
            { label: 'Contacted', count: contacted, color: '#eab308' },
            { label: 'Interview', count: interviewed, color: '#22c55e' },
            { label: 'Hire', count: hired, color: '#10b981' },
        ];
    }

    _computeAttentionItems(candidates) {
        const groups = {
            responses: [],
            unscored: [],
            pending: [],
            failed: [],
        };

        for (const c of candidates) {
            const name = c.name || c.fileName || 'Unknown';

            // New responses
            if (c.responseStatus && c.responseStatus !== 'no_response' && c.disposition !== 'reject') {
                groups.responses.push({
                    id: c.id,
                    name,
                    action: `Response: ${c.responseStatus.replace(/_/g, ' ')}`,
                    color: '#f97316',
                });
            }

            // Failed extraction or scoring
            if (c.extractionStatus === 'error') {
                groups.failed.push({
                    id: c.id,
                    name,
                    action: `Extraction error: ${c.extractionError || 'unknown'}`,
                    color: '#ef4444',
                });
                continue;
            }

            // Check for failed scores
            const scores = c.scores || {};
            for (const [pid, sd] of Object.entries(scores)) {
                if (sd?.status === 'error') {
                    const promptName = this._promptStore?.getById(pid)?.name || pid;
                    groups.failed.push({
                        id: c.id,
                        name,
                        action: `Score error: ${promptName}`,
                        color: '#ef4444',
                    });
                }
            }

            // Pending extraction
            if (c.extractionStatus === 'pending' || c.extractionStatus === 'processing') {
                groups.pending.push({
                    id: c.id,
                    name,
                    action: c.extractionStatus === 'processing' ? 'Extracting...' : 'Awaiting extraction',
                    color: '#94a3b8',
                });
                continue;
            }

            // Unscored (extracted but no scores at all)
            if (c.extractionStatus === 'complete') {
                const project = this._projectStore?.getById(this._projectId);
                const selectedPrompts = project?.selectedPrompts || [];
                const hasAllScores = selectedPrompts.length > 0 &&
                    selectedPrompts.every(pid => scores[pid]?.status === 'complete');
                if (selectedPrompts.length > 0 && !hasAllScores) {
                    const missing = selectedPrompts.filter(pid => !scores[pid] || scores[pid]?.status !== 'complete');
                    groups.unscored.push({
                        id: c.id,
                        name,
                        action: `Missing ${missing.length} score(s)`,
                        color: '#3b82f6',
                    });
                }
            }
        }

        return groups;
    }

    _buildPipeline(stages) {
        const section = document.createElement('div');
        section.className = 'rm-config-section';
        section.innerHTML = '<h3>Pipeline</h3>';

        const pipeline = document.createElement('div');
        pipeline.className = 'rm-pipeline';

        stages.forEach((stage, i) => {
            if (i > 0) {
                const arrow = document.createElement('div');
                arrow.className = 'rm-pipeline-arrow';
                arrow.innerHTML = '&#8250;';
                pipeline.appendChild(arrow);
            }

            const card = document.createElement('div');
            card.className = 'rm-pipeline-stage';
            card.style.borderTopColor = stage.color;
            card.style.borderTopWidth = '3px';
            card.innerHTML = `
                <div class="rm-pipeline-count" style="color:${stage.color}">${stage.count}</div>
                <div class="rm-pipeline-label">${stage.label}</div>
            `;
            card.addEventListener('click', () => {
                this._onSwitchTab('candidates');
            });
            pipeline.appendChild(card);
        });

        section.appendChild(pipeline);
        return section;
    }

    _buildAttention(groups) {
        const section = document.createElement('div');
        section.className = 'rm-config-section';
        section.innerHTML = '<h3>Needs Attention</h3>';

        const allEmpty = !groups.responses.length && !groups.unscored.length &&
            !groups.pending.length && !groups.failed.length;

        if (allEmpty) {
            const empty = document.createElement('div');
            empty.style.cssText = 'color:var(--text-muted);font-size:13px;padding:8px 0;';
            empty.textContent = 'Nothing needs attention right now.';
            section.appendChild(empty);
            return section;
        }

        const groupDefs = [
            { key: 'responses', label: 'New Responses', items: groups.responses },
            { key: 'failed', label: 'Failed', items: groups.failed },
            { key: 'unscored', label: 'Unscored', items: groups.unscored },
            { key: 'pending', label: 'Pending Extraction', items: groups.pending },
        ];

        for (const gd of groupDefs) {
            if (gd.items.length === 0) continue;

            const group = document.createElement('div');
            group.className = 'rm-attention-group';

            const header = document.createElement('div');
            header.className = 'rm-attention-header';
            header.textContent = `${gd.label} (${gd.items.length})`;
            group.appendChild(header);

            for (const item of gd.items) {
                const row = document.createElement('div');
                row.className = 'rm-attention-item';
                row.innerHTML = `
                    <span class="rm-attention-dot" style="background:${item.color}"></span>
                    <span class="rm-attention-name">${this._escHtml(item.name)}</span>
                    <span class="rm-attention-action">${this._escHtml(item.action)}</span>
                `;
                row.addEventListener('click', () => {
                    this._onSelectCandidate(item.id);
                    this._onSwitchTab('candidates');
                });
                group.appendChild(row);
            }

            section.appendChild(group);
        }

        return section;
    }

    _renderActivity() {
        if (!this._activityListEl) return;

        if (this._logEntries.length === 0) {
            this._activityListEl.innerHTML =
                '<div style="color:var(--text-muted);font-size:13px;padding:8px 0;">No recent activity.</div>';
            return;
        }

        this._activityListEl.innerHTML = '';
        for (const entry of this._logEntries) {
            const row = document.createElement('div');
            row.className = 'rm-activity-item';

            const ts = entry.createdAt?.toDate ? entry.createdAt.toDate() : new Date(entry.createdAt);
            const timeStr = this._formatTime(ts);
            const desc = entry.message || entry.type || 'Activity';

            row.innerHTML = `
                <span class="rm-activity-time">${timeStr}</span>
                <span class="rm-activity-text">${this._escHtml(desc)}</span>
            `;
            this._activityListEl.appendChild(row);
        }
    }

    _formatTime(date) {
        if (!date || isNaN(date.getTime())) return '--';
        const now = new Date();
        const diff = now - date;
        if (diff < 60000) return 'just now';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    _escHtml(str) {
        const d = document.createElement('div');
        d.textContent = str;
        return d.innerHTML;
    }

    dispose() {
        if (this._unsubLog) this._unsubLog();
        this._unsubLog = null;
    }
}
